import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import TranslateButton from "./TranslateButton";

const navLinks = [
  { label: "Inicio", href: "/" },
  { label: "Noticias", href: "/actualidad" },
  { label: "Aeropuertos", href: "/aeropuertos" },
  { label: "Aerolíneas", href: "/aerolineas" },
  { label: "Turismo", href: "/turismo" },
  { label: "Carga Aérea", href: "/actualidad?categoria=Carga+Aérea+y+Logística" },
  { label: "Contacto", href: "/contacto" },
];

const socialLinks = [
  { href: "https://www.facebook.com/profile.php?id=61591088725425", icon: "ri-facebook-fill", label: "Facebook" },
  { href: "https://www.instagram.com/aviacioninsightrd?utm_source=qr", icon: "ri-instagram-line", label: "Instagram" },
  { href: "https://x.com/aviacioninsight?s=11&t=_ylQO8qtabGaqp-OhtGSBA", icon: "ri-twitter-x-line", label: "X" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome || menuOpen
          ? "bg-[#0B1F3A] shadow-lg"
          : "bg-transparent"
      }`}
    >
      {/* Top bar */}
      <div
        className={`border-b transition-all duration-300 ${
          scrolled || !isHome ? "border-blue-900/40" : "border-white/10"
        } hidden lg:block`}
      >
        <div className="max-w-screen-xl mx-auto px-6 py-1.5 flex items-center justify-between">
          <span
            className="text-[10px] text-blue-200/40 tracking-wide"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            República Dominicana &amp; el Caribe · Noticias aeronáuticas
          </span>
          <div className="flex items-center gap-2.5">
            {socialLinks.map((s) => (
              <a
                key={s.icon}
                href={s.href}
                target="_blank"
                rel="nofollow noreferrer"
                aria-label={s.label}
                className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/12 rounded-full transition-all cursor-pointer"
              >
                <i className={`${s.icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/7a6028ad-3552-457e-be8d-c77b0b9450c6/f53e079f-ed10-4acb-9346-bd1da877b1e6_apple-touch-icon.png?v=bcaf5114cecaf378812bbdd6646af1ba"
                alt="Aviación Insight RD logo"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="hidden sm:block">
              <p
                className="text-white font-bold text-lg leading-tight tracking-wide whitespace-nowrap"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                AVIACIÓN INSIGHT RD
              </p>
              <p
                className="text-blue-300/70 text-[10px] tracking-widest uppercase"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Noticias Aeronáuticas
              </p>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-end">
            {navLinks.slice(0, 7).map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors rounded-lg cursor-pointer ${
                  location.pathname === link.href
                    ? "text-white bg-white/12"
                    : "text-white/70 hover:text-white hover:bg-white/8"
                }`}
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {link.label}
              </Link>
            ))}

            {/* Publicidad CTA */}
            <Link
              to="/publicidad"
              className={`ml-2 px-4 py-2 text-sm font-bold whitespace-nowrap transition-all rounded-lg cursor-pointer border ${
                location.pathname === "/publicidad"
                  ? "bg-red-600 border-red-600 text-white"
                  : "border-red-500/60 text-red-400 hover:bg-red-600 hover:border-red-600 hover:text-white"
              }`}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <i className="ri-megaphone-line mr-1" />
              Publicidad
            </Link>

            {/* Admin access */}
            <Link
              to="/admin"
              className="ml-1 w-8 h-8 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors cursor-pointer rounded-lg hover:bg-white/8"
              title="Panel de administración"
            >
              <i className="ri-settings-3-line text-sm" />
            </Link>

            {/* Translate toggle */}
            <div className="ml-1">
              <TranslateButton />
            </div>
          </nav>

          {/* Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menú"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-[#0B1F3A] border-t border-blue-900/40 px-4 md:px-6 py-4">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="px-4 py-3 text-sm text-white/75 hover:text-white hover:bg-white/8 rounded-lg transition-colors cursor-pointer"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-3 px-4">
              <TranslateButton />
            </div>
            <div className="flex gap-4 mt-4 px-4">
              {socialLinks.map((s) => (
                <a key={s.icon} href={s.href} target="_blank" rel="nofollow noreferrer"
                  className="w-8 h-8 flex items-center justify-center text-blue-300 hover:text-white cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`} />
                </a>
              ))}
            </div>
            {/* Admin access mobile */}
            <Link
              to="/admin"
              className="mt-3 px-4 py-3 text-sm text-white/50 hover:text-white/80 hover:bg-white/8 rounded-lg transition-colors cursor-pointer flex items-center gap-2"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <i className="ri-settings-3-line text-base" />
              Panel de administración
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}